import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  getInvite,
  acceptInvite,
  revokeInvite,
  type Invite,
  type InviteServiceError,
} from '../services/invite.service';
import { Button, Spinner } from '../components/core';
import toast from 'react-hot-toast';

type PageState = 'loading' | 'not-found' | 'expired' | 'revoked' | 'already-accepted' | 'needs-signin' | 'ready' | 'accepting' | 'revoking' | 'success';

export default function InviteAcceptPage() {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const { user, isLoading: authLoading, signIn } = useAuth();

  const [state, setState] = useState<PageState>('loading');
  const [invite, setInvite] = useState<Invite | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [processingAction, setProcessingAction] = useState<'accept' | 'decline' | null>(null);

  // Load the invite on mount
  useEffect(() => {
    if (!token || authLoading) return;

    const load = async () => {
      try {
        setState('loading');
        setError(null);

        const invite = await getInvite(token);

        if (!invite) {
          setState('not-found');
          return;
        }

        // Check if invite is revoked
        if (invite.isRevoked) {
          setState('revoked');
          return;
        }

        // Check if already accepted
        if (invite.acceptedAt) {
          setState('already-accepted');
          setInvite(invite);
          return;
        }

        // Check if expired
        const now = new Date();
        if (now > invite.expiresAt.toDate()) {
          setState('expired');
          return;
        }

        // If user is not signed in, show sign-in state
        if (!user) {
          setState('needs-signin');
          setInvite(invite);
          return;
        }

        // All checks passed — invite is valid
        setState('ready');
        setInvite(invite);
      } catch (err) {
        const serviceErr = err as InviteServiceError;
        setError(serviceErr.message || 'Failed to load invite');
        setState('not-found');
      }
    };

    load();
  }, [token, user, authLoading]);

  const handleAccept = async () => {
    if (!invite || !user?.email) return;

    setProcessingAction('accept');
    try {
      await acceptInvite(invite.token, invite, user.uid, user.email);
      toast.success('Invite accepted! Redirecting...');
      // Small delay allows the button "Accepting..." text to render
      await new Promise(resolve => setTimeout(resolve, 500));
      navigate('/', { replace: true });
    } catch (err) {
      const serviceErr = err as InviteServiceError;
      setError(serviceErr.message || 'Failed to accept invite');
      toast.error(serviceErr.message || 'Failed to accept invite');
      setProcessingAction(null);
    }
  };

  const handleDecline = async () => {
    if (!invite) return;

    setProcessingAction('decline');
    try {
      await revokeInvite(invite.token);
      toast.success('Invite declined');
      // Small delay allows the button "Declining..." text to render
      await new Promise(resolve => setTimeout(resolve, 500));
      navigate('/', { replace: true });
    } catch (err) {
      const serviceErr = err as InviteServiceError;
      setError(serviceErr.message || 'Failed to decline invite');
      toast.error(serviceErr.message || 'Failed to decline invite');
      setProcessingAction(null);
    }
  };

  // Loading state
  if (state === 'loading' || authLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-green-50">
        <Spinner size="lg" />
      </div>
    );
  }

  // Not found
  if (state === 'not-found') {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-6 bg-green-50 px-6">
        <div className="rounded-lg border border-red-200 bg-white px-8 py-12 text-center shadow-md">
          <div className="mb-4 text-5xl text-red-600">❌</div>
          <h1 className="mb-2 font-display text-2xl font-semibold text-gray-900">Invalid Invite</h1>
          <p className="mb-6 text-gray-600">This invite link is not valid or has expired.</p>
          {error && <p className="mb-4 text-sm text-red-600">{error}</p>}
          <Button onClick={() => navigate('/')}>Back to Home</Button>
        </div>
      </div>
    );
  }

  // Expired
  if (state === 'expired') {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-6 bg-green-50 px-6">
        <div className="rounded-lg border border-amber-200 bg-white px-8 py-12 text-center shadow-md">
          <div className="mb-4 text-5xl">⏰</div>
          <h1 className="mb-2 font-display text-2xl font-semibold text-gray-900">Invite Expired</h1>
          <p className="mb-6 text-gray-600">This invite has expired. Please contact the organization admin for a new invite.</p>
          <Button onClick={() => navigate('/')}>Back to Home</Button>
        </div>
      </div>
    );
  }

  // Revoked
  if (state === 'revoked') {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-6 bg-green-50 px-6">
        <div className="rounded-lg border border-red-200 bg-white px-8 py-12 text-center shadow-md">
          <div className="mb-4 text-5xl">🚫</div>
          <h1 className="mb-2 font-display text-2xl font-semibold text-gray-900">Invite Revoked</h1>
          <p className="mb-6 text-gray-600">This invite is no longer valid. Please contact the organization admin.</p>
          <Button onClick={() => navigate('/')}>Back to Home</Button>
        </div>
      </div>
    );
  }

  // Already accepted
  if (state === 'already-accepted' && invite) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-6 bg-green-50 px-6">
        <div className="rounded-lg border border-green-200 bg-white px-8 py-12 text-center shadow-md">
          <div className="mb-4 text-5xl">✅</div>
          <h1 className="mb-2 font-display text-2xl font-semibold text-gray-900">Already a Member</h1>
          <p className="mb-6 text-gray-600">You&apos;re already a member of this organization.</p>
          <Button onClick={() => navigate('/')}>Go to Org Chooser</Button>
        </div>
      </div>
    );
  }

  // Needs sign-in
  if (state === 'needs-signin' && invite) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-6 bg-green-50 px-6">
        <div className="rounded-lg border border-green-200 bg-white px-8 py-12 text-center shadow-md max-w-md">
          <h1 className="mb-2 font-display text-2xl font-semibold text-gray-900">Sign In Required</h1>
          <p className="mb-4 text-gray-600">You&apos;ve been invited to join an organization as an Operator.</p>
          <p className="mb-6 text-sm text-gray-500">Please sign in with <strong>{invite.email}</strong> to accept the invitation.</p>
          <Button onClick={() => void signIn()}>Sign In</Button>
        </div>
      </div>
    );
  }

  // Ready to accept/decline
  if (state === 'ready' && invite) {
    // Extract org info from the invite doc
    // The org name and slug will be fetched when the invite is validated
    // For now, we show a generic org name — the actual name will come from the accept flow
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-6 bg-green-50 px-6 py-10">
        <div className="w-full max-w-md rounded-lg border border-green-200 bg-white shadow-md overflow-hidden">
          {/* Header with gradient */}
          <div className="bg-gradient-to-br from-green-800 to-green-700 px-8 py-8 text-center text-white">
            <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-lg bg-white/20 font-display text-2xl font-semibold mx-auto">
              🌾
            </div>
            <h2 className="font-display text-2xl font-semibold">
              {invite?.orgName ? `Join ${invite.orgName}` : 'Join Organization'}
            </h2>
          </div>

          {/* Body */}
          <div className="px-8 py-8">
            <div className="mb-6 flex items-center justify-center gap-3 text-gray-700">
              <span className="text-sm font-semibold">Role: Operator</span>
            </div>

            <div className="mb-6 rounded-sm border border-green-200 bg-green-50 px-4 py-3 flex items-center gap-2">
              <span className="text-green-600">✓</span>
              <p className="text-sm text-green-800">Joining as {user?.email}</p>
            </div>

            <p className="mb-6 text-center text-xs text-gray-500">
              {(() => {
                const expiresAt = invite?.expiresAt.toDate();
                if (!expiresAt) return 'Expires soon';
                const hoursLeft = Math.ceil((expiresAt.getTime() - Date.now()) / 3_600_000);
                if (hoursLeft <= 0) return 'Expires now';
                if (hoursLeft < 24) return `Expires in ${hoursLeft} hour${hoursLeft !== 1 ? 's' : ''}`;
                const daysLeft = Math.ceil(hoursLeft / 24);
                return `Expires in ${daysLeft} day${daysLeft !== 1 ? 's' : ''}`;
              })()}
            </p>

            {/* Action buttons */}
            <div className="flex gap-3">
              <Button
                variant="secondary"
                onClick={handleDecline}
                disabled={processingAction !== null}
              >
                {processingAction === 'decline' ? 'Declining...' : 'Decline'}
              </Button>
              <Button
                onClick={handleAccept}
                disabled={processingAction !== null}
              >
                {processingAction === 'accept' ? 'Accepting...' : 'Accept'}
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Fallback
  return (
    <div className="flex min-h-screen items-center justify-center bg-green-50">
      <Spinner size="lg" />
    </div>
  );
}
